import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);
import {
  MagnifyingGlassIcon,
  TicketIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  FireIcon,
  ExclamationCircleIcon,
  CalendarIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import Navigation from './Navigation';
import Footer from './Footer';
import config from '../config/api';
import turnstileConfig from '../config/turnstile';

const TrackTicket = () => {
  const [email, setEmail] = useState('');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);

  // Load Turnstile
  useEffect(() => {
    const loadTurnstile = () => {
      if (!document.getElementById("turnstile-script")) {
        const script = document.createElement("script");
        script.id = "turnstile-script";
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.async = true;
        document.head.appendChild(script);
        script.onload = renderTurnstile;
      } else if (window.turnstile) { renderTurnstile(); }
    };
    const renderTurnstile = () => {
      if (turnstileRef.current && window.turnstile && !widgetIdRef.current) {
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: turnstileConfig.siteKey,
          callback: (token) => setTurnstileToken(token),
          "expired-callback": () => setTurnstileToken(""),
          theme: "dark", language: "vi"
        });
      }
    };
    loadTurnstile();
    return () => { if (widgetIdRef.current && window.turnstile) { window.turnstile.remove(widgetIdRef.current); widgetIdRef.current = null; } };
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    if (!turnstileToken) return;

    setLoading(true);
    setSearched(true);

    try {
      const endpoints = [
        `${config.FALLBACK_API_URL}?email=${encodeURIComponent(email)}`,
        `${config.TRACK_TICKET_URL}?email=${encodeURIComponent(email)}`
      ];

      let success = false;

      for (const endpoint of endpoints) {
        try {
          const response = await fetch(endpoint, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
          });

          if (response.ok) {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const data = await response.json();
              setTickets(data);
              success = true;
              break;
            }
          }
        } catch (fetchError) {
          console.error(`Error ${endpoint}:`, fetchError.message);
        }
      }

      if (!success) {
        setTickets([]);
      }
    } catch (error) {
      console.error('Search error:', error);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusDisplay = (status) => {
    const statusMap = {
      'DRAFT': 'Bản nháp',
      'OPEN': 'Đang mở',
      'PROCESSING': 'Đang xử lý',
      'CLOSE': 'Đã đóng',
      'REOPEN': 'Mở lại'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'DRAFT': 'bg-slate-500/20 text-slate-400 border-slate-500/30',
      'OPEN': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      'PROCESSING': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      'CLOSE': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      'REOPEN': 'bg-purple-500/20 text-purple-400 border-purple-500/30'
    };
    return colors[status] || 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  };

  const getPriorityDisplay = (priority) => {
    const priorityMap = {
      'LOW': 'Thấp',
      'MEDIUM': 'Trung bình',
      'HIGH': 'Cao',
      'URGENT': 'Khẩn cấp'
    };
    return priorityMap[priority] || priority;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'LOW': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'MEDIUM': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      'HIGH': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'URGENT': 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return colors[priority] || 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'URGENT': return <FireIcon className="w-4 h-4" />;
      case 'HIGH': return <ExclamationTriangleIcon className="w-4 h-4" />;
      case 'MEDIUM': return <ClockIcon className="w-4 h-4" />;
      default: return <ExclamationCircleIcon className="w-4 h-4" />;
    }
  };

  const getTypeDisplay = (type) => {
    const typeMap = {
      'BUG': 'Lỗi',
      'FEATURE': 'Tính năng',
      'SUPPORT': 'Hỗ trợ',
      'OTHER': 'Khác'
    };
    return typeMap[type] || type;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return dayjs.utc(dateString).tz('Asia/Ho_Chi_Minh').format('DD/MM/YYYY HH:mm');
  };

  // Selected Ticket Detail View
  if (selectedTicket) {
    return (
      <div className="min-h-screen bg-dark-900">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-24">
          <button
            onClick={() => setSelectedTicket(null)}
            className="flex items-center text-cyan-400 hover:text-cyan-300 mb-6 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Quay lại danh sách
          </button>

          <div className="bg-dark-800/50 backdrop-blur-xl rounded-2xl border border-cyan-500/20 p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">
                #{selectedTicket.id} - {selectedTicket.title}
              </h2>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border ${getStatusColor(selectedTicket.status)}`}>
                {getStatusDisplay(selectedTicket.status)}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-dark-900/50 p-4 rounded-xl border border-cyan-500/10">
                <p className="text-ocean-100/60 text-sm mb-1">Mức độ ưu tiên</p>
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-semibold border ${getPriorityColor(selectedTicket.priority)}`}>
                  {getPriorityIcon(selectedTicket.priority)}
                  {getPriorityDisplay(selectedTicket.priority)}
                </span>
              </div>
              <div className="bg-dark-900/50 p-4 rounded-xl border border-cyan-500/10">
                <p className="text-ocean-100/60 text-sm mb-1">Loại yêu cầu</p>
                <p className="text-white font-medium">{getTypeDisplay(selectedTicket.type)}</p>
              </div>
              <div className="bg-dark-900/50 p-4 rounded-xl border border-cyan-500/10">
                <p className="text-ocean-100/60 text-sm mb-1">Ngày tạo</p>
                <p className="text-white font-medium">{formatDate(selectedTicket.created_at)}</p>
              </div>
              <div className="bg-dark-900/50 p-4 rounded-xl border border-cyan-500/10">
                <p className="text-ocean-100/60 text-sm mb-1">Cập nhật lần cuối</p>
                <p className="text-white font-medium">{formatDate(selectedTicket.updated_at)}</p>
              </div>
            </div>

            {selectedTicket.description && (
              <div className="bg-dark-900/50 p-5 rounded-xl border border-cyan-500/10">
                <h3 className="font-medium text-white mb-3">Mô tả:</h3>
                <p className="text-ocean-100/70 whitespace-pre-wrap">{selectedTicket.description}</p>
              </div>
            )}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-900">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-24 bg-gradient-to-br from-dark-900 via-dark-800 to-ocean-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <TicketIcon className="w-16 h-16 text-cyan-400 mx-auto mb-6" />
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Tra cứu trạng thái liên hệ
          </h1>
          <p className="text-xl text-ocean-100/70 max-w-3xl mx-auto">
            Nhập email bạn đã sử dụng khi liên hệ để xem trạng thái và tiến độ xử lý yêu cầu của bạn.
          </p>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-12 bg-dark-800/50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSearch} className="mb-8">
            <div className="flex">
              <div className="flex-1">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Nhập email của bạn..."
                  required
                  className="w-full px-4 py-3 bg-dark-900 border border-cyan-500/20 rounded-l-xl text-white placeholder-ocean-100/40 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all"
                />
              </div>
              <button
                type="submit"
                disabled={loading || !turnstileToken}
                className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-r-xl hover:from-cyan-600 hover:to-blue-700 focus:ring-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <MagnifyingGlassIcon className="w-5 h-5" />
                )}
              </button>
            </div>
          </form>

          {/* Turnstile Widget */}
          <div className="flex justify-center my-4">
            <div ref={turnstileRef}></div>
          </div>

          {/* Results */}
          {searched && (
            <div className="bg-dark-800/50 backdrop-blur-xl rounded-2xl border border-cyan-500/20 overflow-hidden">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400 mx-auto mb-4"></div>
                  <p className="text-ocean-100/60">Đang tìm kiếm...</p>
                </div>
              ) : tickets.length > 0 ? (
                <div>
                  <div className="px-6 py-4 border-b border-cyan-500/10">
                    <h3 className="text-lg font-semibold text-white">
                      Tìm thấy {tickets.length} yêu cầu liên hệ
                    </h3>
                  </div>
                  <div className="divide-y divide-cyan-500/10">
                    {tickets.map((ticket) => (
                      <div
                        key={ticket.id}
                        onClick={() => setSelectedTicket(ticket)}
                        className="p-6 hover:bg-cyan-500/5 cursor-pointer transition-colors"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h4 className="text-lg font-medium text-white">
                                #{ticket.id} - {ticket.title}
                              </h4>
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold border ${getStatusColor(ticket.status)}`}>
                                {getStatusDisplay(ticket.status)}
                              </span>
                            </div>

                            <div className="flex items-center space-x-4 mb-3">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold border ${getPriorityColor(ticket.priority)}`}>
                                {getPriorityIcon(ticket.priority)}
                                <span className="ml-1">{getPriorityDisplay(ticket.priority)}</span>
                              </span>
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-ocean-100/60 bg-dark-900/50 border border-cyan-500/10">
                                {getTypeDisplay(ticket.type)}
                              </span>
                            </div>

                            <p className="text-ocean-100/60 text-sm line-clamp-2 mb-2">
                              {ticket.description}
                            </p>

                            <div className="flex items-center text-xs text-ocean-100/50 space-x-4">
                              <div className="flex items-center">
                                <CalendarIcon className="w-4 h-4 mr-1" />
                                Tạo: {formatDate(ticket.created_at)}
                              </div>
                              <div className="flex items-center">
                                <CalendarIcon className="w-4 h-4 mr-1" />
                                Cập nhật: {formatDate(ticket.updated_at)}
                              </div>
                            </div>
                          </div>

                          <div className="ml-4 text-right">
                            <div className="text-sm text-cyan-400">
                              Click để xem chi tiết
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="p-8 text-center">
                  <TicketIcon className="w-12 h-12 text-ocean-100/30 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">
                    Không tìm thấy yêu cầu nào
                  </h3>
                  <p className="text-ocean-100/60">
                    Không có yêu cầu liên hệ nào với email: <strong className="text-cyan-400">{email}</strong>
                  </p>
                  <p className="text-ocean-100/50 text-sm mt-2">
                    Vui lòng kiểm tra lại email hoặc liên hệ trực tiếp với chúng tôi.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Instructions */}
      <section className="py-12 bg-dark-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">
            Hướng dẫn tra cứu
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-cyan-500/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 border border-cyan-500/20">
                <span className="text-cyan-400 font-bold">1</span>
              </div>
              <h3 className="font-semibold text-white mb-2">Nhập email</h3>
              <p className="text-ocean-100/60 text-sm">
                Nhập chính xác email bạn đã sử dụng khi gửi yêu cầu liên hệ
              </p>
            </div>

            <div className="text-center">
              <div className="bg-cyan-500/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 border border-cyan-500/20">
                <span className="text-cyan-400 font-bold">2</span>
              </div>
              <h3 className="font-semibold text-white mb-2">Xem kết quả</h3>
              <p className="text-ocean-100/60 text-sm">
                Hệ thống sẽ hiển thị tất cả yêu cầu liên quan đến email của bạn
              </p>
            </div>

            <div className="text-center">
              <div className="bg-cyan-500/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 border border-cyan-500/20">
                <span className="text-cyan-400 font-bold">3</span>
              </div>
              <h3 className="font-semibold text-white mb-2">Theo dõi tiến độ</h3>
              <p className="text-ocean-100/60 text-sm">
                Click vào từng yêu cầu để xem chi tiết và trạng thái xử lý
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TrackTicket;
